import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const images = [{
  url: 'https://cdn.magicpatterns.com/uploads/kPHsCGnNMnaTXMmFGiY54C/Genesis.jpg',
  caption: 'Génesis — El Jardín del Edén'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/kuBKacB72xQD9Tr4W8k6zo/Jerusalem_Antiga.jpg',
  caption: 'Jerusalén Antigua — Vista Aérea'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/eHtA9wq2JtBLk1zAnSwpzk/Mar_da_Galileia.jpg',
  caption: 'Mar de Galilea'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/1UxG4pxfEuD8zNRggRN3qy/Mar_Vermelho.jpg',
  caption: 'Mar Rojo — Cruce del Éxodo'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/fVvqndUMcJtxA7UaDyqpDb/Rio_Jordao.jpg',
  caption: 'Río Jordán'
}];
export function ImageCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };
  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(prevIndex => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = images.length - 1;
      if (nextIndex >= images.length) nextIndex = 0;
      return nextIndex;
    });
  };
  return <section className="py-8 md:py-16 bg-parchment-200 overflow-hidden" data-id="element-235">
      <div className="max-w-6xl mx-auto px-4 sm:px-6" data-id="element-236">
        <div className="text-center mb-6 md:mb-10" data-id="element-237">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-navy-900 mb-4" data-id="element-238">
            Ejemplos de las 280+ imágenes incluidas
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg" data-id="element-239">
            Recorre visualmente los lugares más importantes de las Sagradas
            Escrituras
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto" data-id="element-240">
          {/* Image Container */}
          <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-xl border-2 border-gold-200" data-id="element-241">
            <AnimatePresence initial={false} custom={direction} data-id="element-242">
              <motion.img key={currentIndex} src={images[currentIndex].url} alt={images[currentIndex].caption} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={1} onDragEnd={(e, {
              offset,
              velocity
            }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }} className="absolute w-full h-full object-cover" data-id="element-243" />
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg text-navy-900 hover:bg-white hover:text-gold-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500" onClick={() => paginate(-1)} aria-label="Imagen anterior" data-id="element-244">
              <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" data-id="element-245" />
            </button>
            <button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg text-navy-900 hover:bg-white hover:text-gold-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500" onClick={() => paginate(1)} aria-label="Siguiente imagen" data-id="element-246">
              <ChevronRight className="w-6 h-6 md:w-8 md:h-8" data-id="element-247" />
            </button>
          </div>

          {/* Caption and Counter */}
          <div className="mt-3 md:mt-6 flex flex-col items-center" data-id="element-248">
            <h3 className="font-heading text-lg md:text-2xl font-bold text-navy-900 mb-2 text-center" data-id="element-249">
              {images[currentIndex].caption}
            </h3>
            <div className="flex items-center gap-4" data-id="element-250">
              <span className="text-sm font-medium text-gray-500" data-id="element-251">
                {currentIndex + 1} / {images.length}
              </span>
            </div>

            {/* Dot Indicators */}
            <div className="flex gap-2 mt-2 md:mt-4" data-id="element-252">
              {images.map((_, index) => <button key={index} onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }} className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-gold-600 w-5 md:w-6' : 'bg-gray-300 hover:bg-gold-300'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-253" />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}